import axios from 'axios';

const PORTAL_TOKEN_KEY = 'edupro-portal-token';

export function getPortalToken(): string | null {
  return localStorage.getItem(PORTAL_TOKEN_KEY);
}
export function setPortalToken(token: string | null) {
  if (token) localStorage.setItem(PORTAL_TOKEN_KEY, token);
  else localStorage.removeItem(PORTAL_TOKEN_KEY);
}

// Dedicated instance — carries only the PIN token, never the admin headers.
const portalAxios = axios.create({ baseURL: '/api/v1' });
portalAxios.interceptors.request.use((config) => {
  const t = getPortalToken();
  if (t) config.headers.set('Authorization', `Bearer ${t}`);
  return config;
});

export interface PortalStudent {
  id: string;
  admissionNumber: string;
  studentName: string;
  gender: string | null;
  dateOfBirth: string | null;
  bloodGroup: string | null;
  photoUrl: string | null;
  status: string;
  className: string | null;
  sectionName: string | null;
  rollNumber: string | null;
}

export interface PortalMe {
  role: 'student' | 'parent';
  student?: PortalStudent | null;
  parent?: {
    id: string;
    name: string;
    relation: string | null;
    phone: string;
    email: string | null;
  } | null;
}

export interface PortalSession {
  token: string;
  user: { id: string; name: string; role: 'student' | 'parent' };
}

export interface PortalAttendance {
  summary: {
    present: number;
    absent: number;
    late: number;
    half_day: number;
    holiday: number;
    workingDays: number;
    percentage: number | null;
  };
  recent: { date: string; status: string; note: string | null }[];
}

export interface PortalResultSubject {
  subject: string;
  marksObtained: number | null;
  maxMarks: number;
  passMarks: number;
  grade: string | null;
  isAbsent: boolean;
  passed: boolean;
}

export interface PortalResultExam {
  examId: string;
  name: string;
  startDate: string | null;
  subjects: PortalResultSubject[];
  totalObtained: number;
  totalMax: number;
  percentage: number;
  grade: string;
  passed: boolean;
  rank: number | null;
  reportCardUrl: string | null;
}

export interface PortalResults {
  exams: PortalResultExam[];
}

export interface PortalTimetableSlot {
  id: string;
  day: string;
  date: string | null;
  startTime: string;
  endTime: string;
  type: 'Lecture' | 'Break';
  subjectId: string | null;
  subject: string | null;
  teacherId: string | null;
  teacher: string;
}

export interface PortalTimetable {
  enrolled: boolean;
  grid: {
    section: { id: string; name: string; classId: string; className: string };
    days: string[];
    slots: Record<string, PortalTimetableSlot[]>;
    hasData: boolean;
  } | null;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

// ───── Online Exams (student runtime) ────────────────────────────────────────
// Fresh, from-scratch feature — fully separate from offline exam results
// above (own tables, own endpoints, no merged aggregation). Every time gate
// is enforced server-side; the client only renders what the server sends.
export type OnlineExamWindowStatus = 'upcoming' | 'open' | 'closed';
export type OnlineExamAttemptStatus = 'not_started' | 'in_progress' | 'submitted';

export interface AvailableOnlineExam {
  id: string;
  name: string;
  subjectName: string;
  durationMinutes: number;
  startDatetime: string;
  endDatetime: string;
  windowStatus: OnlineExamWindowStatus;
  attemptStatus: OnlineExamAttemptStatus;
  score: number | null;
}

export interface OnlineExamAttemptChoice {
  id: string;
  choiceText: string;
  orderIndex: number;
}

export interface OnlineExamAttemptQuestion {
  id: string;
  questionText: string;
  marks: number;
  orderIndex: number;
  choices: OnlineExamAttemptChoice[];
}

export interface OnlineExamAttempt {
  exam: { id: string; name: string; durationMinutes: number; startDatetime: string; endDatetime: string };
  /** Server-computed — informational only. The server re-checks the real deadline on submit regardless of client countdown state. */
  deadline: string;
  startedAt: string;
  questions: OnlineExamAttemptQuestion[];
}

export interface OnlineExamSubmitResult {
  submitted: boolean;
  score: number;
  totalMarks: number;
}

export interface OnlineExamResultSummary {
  total_questions: number;
  total_marks: number;
  obtained_marks: number;
  correct_questions_count: number;
  incorrect_questions_count: number;
}

/** No correct choice_id anywhere — a score breakdown, not an answer-key reveal. */
export interface OnlineExamResult {
  summary: OnlineExamResultSummary;
  correct_answers: { question_id: string; marks_earned: number }[];
  in_correct_answers: { question_id: string; marks_lost: number }[];
}

export const OnlineExamsPortalApi = {
  available: async (): Promise<AvailableOnlineExam[]> =>
    unwrap(await portalAxios.get('/portal/online-exams/available')),
  questions: async (id: string): Promise<OnlineExamAttempt> =>
    unwrap(await portalAxios.get(`/portal/online-exams/${id}/questions`)),
  submit: async (
    id: string,
    answers: { questionId: string; selectedChoiceId: string | null }[],
  ): Promise<OnlineExamSubmitResult> =>
    unwrap(await portalAxios.post(`/portal/online-exams/${id}/submit`, { answers })),
  result: async (id: string): Promise<OnlineExamResult> =>
    unwrap(await portalAxios.get(`/portal/online-exams/${id}/result`)),
};

export const PortalApi = {
  studentLogin: async (
    schoolCode: string,
    admissionNumber: string,
    pin: string,
  ): Promise<PortalSession> =>
    unwrap(
      await axios.post('/api/v1/auth/student-login', {
        schoolCode,
        admissionNumber,
        pin,
      }),
    ),
  parentLogin: async (
    schoolCode: string,
    mobile: string,
    pin: string,
  ): Promise<PortalSession> =>
    unwrap(
      await axios.post('/api/v1/auth/parent-login', { schoolCode, mobile, pin }),
    ),
  me: async (): Promise<PortalMe> => unwrap(await portalAxios.get('/portal/me')),
  attendance: async (): Promise<PortalAttendance> =>
    unwrap(await portalAxios.get('/portal/attendance')),
  results: async (): Promise<PortalResults> =>
    unwrap(await portalAxios.get('/portal/results')),
  timetable: async (): Promise<PortalTimetable> =>
    unwrap(await portalAxios.get('/portal/timetable')),
};
